// ========================================
// GERENCIAMENTO DE USUÁRIOS - VERSÃO COMPLETA
// frontend/src/components/GerenciarUsuarios.jsx
// ========================================

import React, { useState, useEffect } from 'react';
import Loading from './Loading';
import {
  Users,
  CheckCircle,
  XCircle,
  Shield,
  ShieldCheck,
  Eye,
  EyeOff,
  FileText,
  BarChart3,
  UserCheck,
  UserPlus,
  Edit3,
  AlertCircle,
  Copy,
  Check,
  Key,
  X,
  Table2,
  Target,
  Save,
} from 'lucide-react';
import Modal from './Modal';
import { api } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

// ========================================
// PROTEÇÃO DE ACESSO - SÓ DIRETOR E ADMINISTRATIVO
// ========================================
const ProtecaoAcesso = ({ children, usuario }) => {
  const permitido = ['Diretoria', 'Admin'].includes(usuario?.departamento);

  if (!permitido) {
    return (
      <div className="px-8 py-6 bg-gray-100 min-h-screen">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center max-w-2xl mx-auto mt-20">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-red-800 mb-2">Acesso Negado</h2>
          <p className="text-red-700 mb-4">
            Apenas usuários dos departamentos <strong>Diretoria</strong> e <strong>Admin</strong> podem acessar esta área.
          </p>
          <p className="text-sm text-red-600">
            Seu departamento: <strong>{usuario?.departamento || 'Não definido'}</strong>
          </p>
        </div>
      </div>
    );
  }

  return children;
};

// ========================================
// MODAL EDITAR PERMISSÕES
// ========================================
const EditarUsuarioModal = ({ usuario, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    pode_criar_pedido: false,
    pode_ver_tabela_admin: false,
    pode_ver_pedidos: false,
    pode_ver_dashboard: false,
    pode_aprovar_usuarios: false,
    pode_gerenciar_tabelas: false,
    pode_aprovar_clientes: false,
    pode_alterar_responsavel: false,
    pode_vender_produtos_especiais: false,
    pode_vender_abaixo_minimo: false,
    pode_ver_campanhas: false,
    pode_trocar_cliente_pedido: false,
    fluxo_automatico: false,
    regiao_acesso: 'Todos',
    departamento: '',
    ativo: true,
  });
  const [modalInfo, setModalInfo] = useState({ open: false, type: 'info', title: '', message: '' });

  useEffect(() => {
    if (usuario) {
      setForm({
        pode_criar_pedido: !!usuario.pode_criar_pedido,
        pode_ver_tabela_admin: !!usuario.pode_ver_tabela_admin,
        pode_ver_pedidos: !!usuario.pode_ver_pedidos,
        pode_ver_dashboard: !!usuario.pode_ver_dashboard,
        pode_aprovar_usuarios: !!usuario.pode_aprovar_usuarios,
        pode_gerenciar_tabelas: !!usuario.pode_gerenciar_tabelas,
        pode_aprovar_clientes: !!usuario.pode_aprovar_clientes,
        pode_alterar_responsavel: !!usuario.pode_alterar_responsavel,
        pode_vender_produtos_especiais: !!usuario.pode_vender_produtos_especiais,
        pode_vender_abaixo_minimo: !!usuario.pode_vender_abaixo_minimo,
        pode_ver_campanhas: !!usuario.pode_ver_campanhas,
        pode_trocar_cliente_pedido: !!usuario.pode_trocar_cliente_pedido,
        fluxo_automatico: !!usuario.fluxo_automatico,
        regiao_acesso: usuario.regiao_acesso || 'Todos',
        departamento: usuario.departamento || '',
        ativo: usuario.ativo !== false,
      });
    }
  }, [usuario]);

  if (!usuario) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/usuarios/${usuario.id}/permissoes`, form);
      setModalInfo({
        open: true,
        type: 'success',
        title: 'Sucesso!',
        message: 'Permissões atualizadas com sucesso!',
        onCloseCallback: () => { if (onUpdated) onUpdated(); }
      });
    } catch (error) {
      console.error('Erro ao atualizar permissões:', error);
      setModalInfo({
        open: true,
        type: 'error',
        title: 'Erro',
        message: error.message || 'Erro ao atualizar permissões'
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal box */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 z-10 transform transition-all duration-200 animate-scaleIn"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:scale-110 transition-all"
          aria-label="Fechar"
          title="Fechar"
        >
          <X size={20} />
        </button>

        {/* Header com ícone */}
        <div className="flex flex-col items-center gap-2 mb-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
            <Edit3 size={24} className="text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 text-center">Editar Permissões</h2>
          <p className="text-sm text-gray-500 text-center">
            <span className="font-semibold text-slate-700">{usuario.username_ldap}</span> — {usuario.nome_completo}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Departamento</p>
              <select
                name="departamento"
                value={form.departamento}
                onChange={handleChange}
                className="w-full border-2 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="">Selecione...</option>
                <option value="Diretoria">Diretoria</option>
                <option value="Admin">Admin</option>
                <option value="Administrativo">Administrativo</option>
                <option value="Vendas">Vendas</option>
                <option value="Representante">Representante</option>
              </select>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Região de acesso</p>
              <select
                name="regiao_acesso"
                value={form.regiao_acesso}
                onChange={handleChange}
                className="w-full border-2 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-colors"
              >
                <option value="Todos">Todos</option>
                <option value="Santa Catarina">Santa Catarina</option>
                <option value="Parana">Parana</option>
              </select>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Status</p>
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  name="ativo"
                  checked={form.ativo}
                  onChange={handleChange}
                  className="w-4 h-4"
                />
                {form.ativo ? <span className="text-emerald-600 font-semibold">Ativo</span> : <span className="text-rose-600 font-semibold">Inativo</span>}
              </label>
            </div>
          </div>

          <div className="border-t-2 pt-4">
            <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide">Permissões</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_criar_pedido" checked={form.pode_criar_pedido} onChange={handleChange} className="w-4 h-4" />
                Pode criar pedidos
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_ver_tabela_admin" checked={form.pode_ver_tabela_admin} onChange={handleChange} className="w-4 h-4" />
                Pode ver tabela administrativa
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_ver_pedidos" checked={form.pode_ver_pedidos} onChange={handleChange} className="w-4 h-4" />
                Pode ver pedidos
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_ver_dashboard" checked={form.pode_ver_dashboard} onChange={handleChange} className="w-4 h-4" />
                Pode ver dashboard
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_aprovar_usuarios" checked={form.pode_aprovar_usuarios} onChange={handleChange} className="w-4 h-4" />
                Pode aprovar usuários
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_gerenciar_tabelas" checked={form.pode_gerenciar_tabelas} onChange={handleChange} className="w-4 h-4" />
                Pode gerenciar tabelas
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_aprovar_clientes" checked={form.pode_aprovar_clientes} onChange={handleChange} className="w-4 h-4" />
                Pode aprovar clientes
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_alterar_responsavel" checked={form.pode_alterar_responsavel} onChange={handleChange} className="w-4 h-4" />
                Pode alterar responsável
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_vender_produtos_especiais" checked={form.pode_vender_produtos_especiais} onChange={handleChange} className="w-4 h-4" />
                Pode vender produtos especiais
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_vender_abaixo_minimo" checked={form.pode_vender_abaixo_minimo} onChange={handleChange} className="w-4 h-4" />
                Pode vender abaixo do preço mín.
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_ver_campanhas" checked={form.pode_ver_campanhas} onChange={handleChange} className="w-4 h-4" />
                Pode ver campanhas
              </label>
              <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                <input type="checkbox" name="pode_trocar_cliente_pedido" checked={form.pode_trocar_cliente_pedido} onChange={handleChange} className="w-4 h-4" />
                Pode trocar cliente do pedido
              </label>
            </div>
          </div>

          {/* Seção de Fluxo Automático - Só para departamentos elegíveis */}
          {['Vendas', 'Diretoria', 'Admin', 'Administrativo'].includes(form.departamento) && (
            <div className="border-t-2 pt-4 mt-4">
              <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-600" />
                Fluxo Automático de Aprovação
              </p>
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    name="fluxo_automatico" 
                    checked={form.fluxo_automatico} 
                    onChange={handleChange} 
                    className="w-5 h-5 text-emerald-600" 
                  />
                  <div>
                    <span className="font-semibold text-emerald-800">Habilitar Fluxo Automático</span>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Orçamentos serão aprovados automaticamente se: status VIÁVEL, BOA VENDA ou EXCELENTE + valor até R$ 10.000
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Novos clientes serão aprovados automaticamente com limite de R$ 5.000
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          <div className="mt-5 w-full flex gap-3 pt-2">
            <button 
              type="button" 
              onClick={onClose} 
              className="flex-1 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-600 hover:shadow-lg transition-all"
            >
              Salvar
            </button>
          </div>
        </form>
      </div>

      {/* Modal de feedback */}
      <Modal
        open={modalInfo.open}
        type={modalInfo.type}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => {
          setModalInfo({ ...modalInfo, open: false });
          if (modalInfo.onCloseCallback) modalInfo.onCloseCallback();
        }}
      />
    </div>
  );
};

// ========================================
// MODAL EDITAR DADOS DO USUÁRIO
// ========================================
const EditarDadosModal = ({ usuario, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    nome_completo: '',
    email: '',
    telefone: '',
    cpf_cnpj: '',
    departamento: '',
    cargo: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [modalInfo, setModalInfo] = useState({ open: false, type: 'info', title: '', message: '' });

  useEffect(() => {
    if (usuario) {
      setForm({
        nome_completo: usuario.nome_completo || '',
        email: usuario.email || '',
        telefone: usuario.telefone || '',
        cpf_cnpj: usuario.cpf_cnpj || '',
        departamento: usuario.departamento || '',
        cargo: usuario.cargo || '',
      });
    }
  }, [usuario]);

  if (!usuario) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.put(`/usuarios/${usuario.id}/dados`, form);
      setModalInfo({
        open: true,
        type: 'success',
        title: 'Sucesso!',
        message: 'Dados atualizados com sucesso!',
        onCloseCallback: () => { if (onUpdated) onUpdated(); }
      });
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      setModalInfo({
        open: true,
        type: 'error',
        title: 'Erro',
        message: error.message || 'Erro ao atualizar dados'
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal box */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 z-10 transform transition-all duration-200 animate-scaleIn"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:scale-110 transition-all"
          aria-label="Fechar"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
            <Edit3 className="text-white" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Editar Dados</h2>
            <p className="text-sm text-gray-500">{usuario.nome_completo}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome Completo - Bloqueado para edição */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo</label>
            <input
              type="text"
              name="nome_completo"
              value={form.nome_completo}
              disabled
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
              title="O nome não pode ser alterado"
            />
          </div>

          {/* E-mail */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors"
              required
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone</label>
            <input
              type="text"
              name="telefone"
              value={form.telefone}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>

          {/* CPF/CNPJ */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">CPF/CNPJ</label>
            <input
              type="text"
              name="cpf_cnpj"
              value={form.cpf_cnpj}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>

          {/* Departamento */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Departamento</label>
            <select
              name="departamento"
              value={form.departamento}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors"
              required
            >
              <option value="">Selecione...</option>
              <option value="Admin">Admin</option>
              <option value="Diretoria">Diretoria</option>
              <option value="Administrativo">Administrativo</option>
              <option value="Vendas">Vendas</option>
              <option value="Representante">Representante</option>
            </select>
          </div>

          {/* Cargo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Cargo</label>
            <input
              type="text"
              name="cargo"
              value={form.cargo}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>

          {/* Botões */}
          <div className="mt-5 w-full flex gap-3 pt-2">
            <button 
              type="button" 
              onClick={onClose} 
              className="flex-1 py-2.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={salvando}
              className="flex-1 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-500 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-teal-600 hover:shadow-lg transition-all disabled:opacity-50"
            >
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de feedback */}
      <Modal
        open={modalInfo.open}
        type={modalInfo.type}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => {
          setModalInfo({ ...modalInfo, open: false });
          if (modalInfo.onCloseCallback) modalInfo.onCloseCallback();
        }}
      />
    </div>
  );
};

// ========================================
// FORM NOVO USUÁRIO
// ========================================
const gerarProximoCodigo = (usuarios) => {
  if (!usuarios || usuarios.length === 0) return 'USR001';

  const ordenados = [...usuarios].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  const ultimo = ordenados[ordenados.length - 1];
  const base = ultimo.codigo_id || String(ultimo.id || 0);

  const match = String(base).match(/^([A-Za-z]*)(\d+)$/);
  if (match) {
    const prefix = match[1] || '';
    const numAtual = parseInt(match[2], 10);
    const proximo = numAtual + 1;
    const width = match[2].length;
    const numStr = String(proximo).padStart(width, '0');
    return `${prefix}${numStr}`;
  }

  const num = parseInt(base, 10);
  if (!isNaN(num)) return String(num + 1);

  return 'USR001';
};

const NovoUsuarioForm = ({ onCriado, usuarios }) => {
  const [form, setForm] = useState({
    codigo_id: '',
    username_ldap: '',
    nome_completo: '',
    email: '',
    telefone: '',
    cpf_cnpj: '',
    departamento: '',
    cargo: '',
    regiao_acesso: 'Todos',
    pode_criar_pedido: false,
    pode_ver_tabela_admin: false,
    pode_ver_pedidos: false,
    pode_ver_dashboard: false,
    pode_aprovar_usuarios: false,
    pode_gerenciar_tabelas: false,
    pode_aprovar_clientes: false,
    pode_alterar_responsavel: false,
    pode_vender_produtos_especiais: false,
    pode_vender_abaixo_minimo: false,
    pode_ver_campanhas: false,
    pode_trocar_cliente_pedido: false,
    fluxo_automatico: false,
  });

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [senhaProvisoria, setSenhaProvisoria] = useState('');
  const [usuarioCriado, setUsuarioCriado] = useState('');
  const [copiado, setCopiado] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const copiarSenha = () => {
    navigator.clipboard.writeText(senhaProvisoria);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setSenhaProvisoria('');
    setUsuarioCriado('');
    setLoading(true);

    try {
      const response = await api.post('/usuarios', form);

      if (!response.success) {
        throw new Error(response.message || 'Erro ao criar usuário');
      }

      setSenhaProvisoria(response.senhaProvisoria || '');
      setUsuarioCriado(form.username_ldap);

      const nextCodigo = gerarProximoCodigo([...usuarios, response.usuario]);

      setForm({
        codigo_id: nextCodigo,
        username_ldap: '',
        nome_completo: '',
        email: '',
        telefone: '',
        departamento: '',
        cargo: '',
        regiao_acesso: 'Todos',
        pode_criar_pedido: false,
        pode_ver_tabela_admin: false,
        pode_ver_pedidos: false,
        pode_ver_dashboard: false,
        pode_aprovar_usuarios: false,
        pode_gerenciar_tabelas: false,
        pode_aprovar_clientes: false,
        pode_alterar_responsavel: false,
        pode_vender_produtos_especiais: false,
        pode_vender_abaixo_minimo: false,
        pode_ver_campanhas: false,
        pode_trocar_cliente_pedido: false,
        fluxo_automatico: false,
      });

      if (onCriado) onCriado();
    } catch (error) {
      console.error(error);
      setErro(error.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 w-full hover:shadow-lg transition-all duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-cyan-600 rounded-full"></div>
        <div>
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Cadastrar novo usuário</h2>
          <p className="text-sm text-slate-500">Uma senha provisória será gerada automaticamente</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Código ID *</label>
            <input type="number" name="codigo_id" value={form.codigo_id} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="Ex: 10" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Usuário (LDAP) *</label>
            <input name="username_ldap" value={form.username_ldap} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="joao.silva" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Região de acesso</label>
            <select name="regiao_acesso" value={form.regiao_acesso} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
              <option value="Todos">Todos</option>
              <option value="Santa Catarina">Santa Catarina</option>
              <option value="Parana">Parana</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Nome completo *</label>
            <input name="nome_completo" value={form.nome_completo} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">E-mail</label>
            <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">CPF/CNPJ</label>
            <input name="cpf_cnpj" value={form.cpf_cnpj} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="000.000.000-00" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Telefone</label>
            <input name="telefone" value={form.telefone} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="(00) 00000-0000" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Departamento</label>
            <select name="departamento" value={form.departamento} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
                <option value="">Selecione...</option>
                <option value="Diretoria">Diretoria</option>
                <option value="Admin">Admin</option>
                <option value="Administrativo">Administrativo</option>
                <option value="Vendas">Vendas</option>
                <option value="Representante">Representante</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Cargo</label>
            <input name="cargo" value={form.cargo} onChange={handleChange} className="w-full border-2 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" placeholder="Ex: Vendedor, Coordenador..." />
          </div>
        </div>

        <div className="border-t-2 pt-5">
          <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide">Permissões</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_criar_pedido" checked={form.pode_criar_pedido} onChange={handleChange} className="w-4 h-4" />
              <span>Pode criar pedidos</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_ver_tabela_admin" checked={form.pode_ver_tabela_admin} onChange={handleChange} className="w-4 h-4" />
              <span>Pode ver tabela administrativa</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_ver_pedidos" checked={form.pode_ver_pedidos} onChange={handleChange} className="w-4 h-4" />
              <span>Pode ver pedidos</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_ver_dashboard" checked={form.pode_ver_dashboard} onChange={handleChange} className="w-4 h-4" />
              <span>Pode ver dashboard</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_aprovar_usuarios" checked={form.pode_aprovar_usuarios} onChange={handleChange} className="w-4 h-4" />
              <span>Pode aprovar usuários</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_gerenciar_tabelas" checked={form.pode_gerenciar_tabelas} onChange={handleChange} className="w-4 h-4" />
              <span>Pode gerenciar tabelas</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_aprovar_clientes" checked={form.pode_aprovar_clientes} onChange={handleChange} className="w-4 h-4" />
              <span>Pode aprovar clientes</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_alterar_responsavel" checked={form.pode_alterar_responsavel} onChange={handleChange} className="w-4 h-4" />
              <span>Pode alterar responsável</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_vender_produtos_especiais" checked={form.pode_vender_produtos_especiais} onChange={handleChange} className="w-4 h-4" />
              <span>Pode vender produtos especiais</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_vender_abaixo_minimo" checked={form.pode_vender_abaixo_minimo} onChange={handleChange} className="w-4 h-4" />
              <span>Pode vender abaixo do preço mín.</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_ver_campanhas" checked={form.pode_ver_campanhas} onChange={handleChange} className="w-4 h-4" />
              <span>Pode ver campanhas</span>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" name="pode_trocar_cliente_pedido" checked={form.pode_trocar_cliente_pedido} onChange={handleChange} className="w-4 h-4" />
              <span>Pode trocar cliente do pedido</span>
            </label>
          </div>
        </div>

        {/* Seção de Fluxo Automático - Só para departamentos elegíveis */}
        {['Vendas', 'Diretoria', 'Admin', 'Administrativo'].includes(form.departamento) && (
          <div className="border-t-2 pt-5">
            <p className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-600" />
              Fluxo Automático de Aprovação
            </p>
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="fluxo_automatico" 
                  checked={form.fluxo_automatico} 
                  onChange={handleChange} 
                  className="w-5 h-5 text-emerald-600" 
                />
                <div>
                  <span className="font-semibold text-emerald-800">Habilitar Fluxo Automático</span>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Orçamentos serão aprovados automaticamente se: status VIÁVEL, BOA VENDA ou EXCELENTE + valor até R$ 10.000
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Novos clientes serão aprovados automaticamente com limite de R$ 5.000
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}

        {erro && (
          <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">Erro ao criar usuário:</p>
              <p>{erro}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-3">
          <button type="submit" disabled={loading} className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-bold disabled:opacity-50 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 flex items-center gap-2">
            {loading ? 'Criando...' : <><UserPlus size={18} />Criar usuário</>}
          </button>
        </div>
      </form>

      {senhaProvisoria && (
        <div className="mt-6 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-3 border-amber-400 rounded-xl shadow-lg">
          <p className="font-bold text-amber-900 mb-4 flex items-center gap-2 text-xl">
            <CheckCircle size={24} />
            Usuário criado com sucesso!
          </p>
          <div className="bg-white p-5 rounded-xl border-2 border-amber-300 shadow-sm">
            <p className="text-sm text-slate-700 mb-3">
              <span className="font-bold text-slate-800">Usuário LDAP:</span>{' '}
              <span className="font-mono text-lg text-blue-600 bg-blue-50 px-3 py-1 rounded">{usuarioCriado}</span>
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm text-slate-700 mb-1">
                  <span className="font-bold text-slate-800">Senha provisória:</span>
                </p>
                <div className="bg-amber-100 px-4 py-3 rounded-lg border-2 border-amber-300">
                  <p className="font-mono font-bold text-2xl text-amber-900 text-center tracking-wider">
                    {senhaProvisoria}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={copiarSenha}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-semibold"
              >
                {copiado ? <><Check size={18} />Copiado!</> : <><Copy size={18} />Copiar</>}
              </button>
            </div>
          </div>
          <div className="mt-4 p-4 bg-amber-100 rounded-lg border border-amber-300">
            <p className="text-sm text-amber-900 flex items-start gap-2">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <span>
                <strong>IMPORTANTE:</strong> Envie ao usuário o <strong>username LDAP</strong> e esta <strong>senha provisória</strong>. 
                No primeiro login, o sistema irá <strong>obrigar</strong> a criação de uma nova senha pessoal.
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// TABELA DE USUÁRIOS
// ========================================
const TabelaUsuarios = ({ usuarios, onEditarPermissoes, onEditarDados, onResetarSenha }) => {
  const [senhaCopiada, setSenhaCopiada] = useState(null);

  const formatarData = (d) => {
    if (!d) return 'Nunca acessou';
    const dt = new Date(d);
    return dt.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copiarSenhaProvisoria = (usuario) => {
    if (usuario.senha_provisoria) {
      navigator.clipboard.writeText(usuario.senha_provisoria);
      setSenhaCopiada(usuario.id);
      setTimeout(() => setSenhaCopiada(null), 2000);
    }
  };

  if (!usuarios || usuarios.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-12 text-center hover:shadow-lg transition-all duration-300">
        <Users size={64} className="mx-auto text-slate-300 mb-4 animate-pulse" />
        <p className="text-lg text-slate-600 font-semibold mb-2">Nenhum usuário cadastrado</p>
        <p className="text-sm text-slate-400">Clique em "Novo usuário" para começar</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300">
      <div className="bg-gradient-to-r from-cyan-400 to-cyan-500 px-6 py-4">
        <h2 className="text-white text-lg font-bold uppercase tracking-wide flex items-center gap-2">
          <Users size={20} />
          Usuários Cadastrados ({usuarios.length})
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              <th className="text-left py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Usuário</th>
              <th className="text-left py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Nome</th>
              <th className="text-center py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Departamento</th>
              <th className="text-center py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Região</th>
              <th className="text-center py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Permissões</th>
              <th className="text-left py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Último acesso</th>
              <th className="text-center py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Status</th>
              <th className="text-center py-3 px-4 font-bold text-xs text-slate-600 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u, idx) => (
              <tr key={u.id} className={`border-b hover:bg-blue-50 transition-all duration-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <td className="py-3 px-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-800">{u.username_ldap}</span>
                    <span className="text-xs text-slate-400">{u.codigo_id || ''}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-slate-700 font-medium">{u.nome_completo}</td>
                <td className="py-3 px-4 text-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    u.departamento === 'Admin' ? 'bg-purple-100 text-purple-700' :
                    u.departamento === 'Diretoria' ? 'bg-amber-100 text-amber-700' :
                    u.departamento === 'Vendas' ? 'bg-blue-100 text-blue-700' :
                    u.departamento === 'Representante' ? 'bg-cyan-100 text-cyan-700' :
                    u.departamento === 'Administrativo' ? 'bg-green-100 text-green-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>{u.departamento || '-'}</span>
                </td>
                <td className="py-3 px-4 text-center">
                  <span className="px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 text-xs font-semibold whitespace-nowrap">{u.regiao_acesso || 'Todos'}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2 text-slate-500 justify-center">
                    {u.pode_criar_pedido && <FileText size={18} title="Criar pedidos" />}
                    {u.pode_ver_tabela_admin && <Shield size={18} title="Ver tabela admin" />}
                    {u.pode_ver_pedidos && <Eye size={18} title="Ver pedidos" />}
                    {u.pode_ver_dashboard && <BarChart3 size={18} title="Ver dashboard" />}
                    {u.pode_aprovar_usuarios && <UserCheck size={18} title="Aprovar usuários" />}
                    {u.pode_gerenciar_tabelas && <Table2 size={18} title="Gerenciar tabelas" />}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600">
                      {formatarData(u.data_ultimo_acesso)}
                    </span>
                    
                    {/* ========================================
                        BOTÃO DE COPIAR SENHA PROVISÓRIA
                        Só aparece se precisa_trocar_senha = true E tem senha_provisoria
                    ======================================== */}
                    {u.precisa_trocar_senha && u.senha_provisoria && (
                      <button
                        type="button"
                        onClick={() => copiarSenhaProvisoria(u)}
                        className={`p-1.5 rounded-lg transition-all ${
                          senhaCopiada === u.id 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-amber-100 text-amber-600 hover:bg-amber-200'
                        }`}
                        title={senhaCopiada === u.id ? 'Senha copiada!' : 'Copiar senha provisória'}
                      >
                        {senhaCopiada === u.id ? (
                          <Check size={16} />
                        ) : (
                          <Key size={16} />
                        )}
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4 text-center">
                  {u.ativo ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                      <CheckCircle size={16} /> Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold">
                      <XCircle size={16} /> Inativo
                    </span>
                  )}
                </td>
                <td className="py-3 px-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      type="button"
                      onClick={() => onEditarDados(u)}
                      className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 hover:scale-105 hover:shadow-md transition-all duration-200"
                      title="Editar dados do usuário"
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditarPermissoes(u)}
                      className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 hover:shadow-md transition-all duration-200"
                      title="Editar permissões do usuário"
                    >
                      <Shield size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onResetarSenha(u)}
                      className="p-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 hover:scale-105 hover:shadow-md transition-all duration-200"
                      title="Resetar senha do usuário"
                    >
                      <Key size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========================================
// COMPONENTE PRINCIPAL
// ========================================
const GerenciarUsuarios = ({ embedded = false }) => {
  const { usuario: usuarioLogado } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('todos');
  const [erro, setErro] = useState('');
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [showModalDados, setShowModalDados] = useState(false);
  const [modalInfo, setModalInfo] = useState({ open: false, type: 'info', title: '', message: '' });
  
  // Estados para Gerenciar Metas
  const [usuariosMetas, setUsuariosMetas] = useState([]);
  const [loadingMetas, setLoadingMetas] = useState(false);
  const [metasEditadas, setMetasEditadas] = useState({});
  const [metasOriginais, setMetasOriginais] = useState({}); // Para comparar se houve alteração
  const [salvandoMetas, setSalvandoMetas] = useState({});


  useEffect(() => {
    carregarUsuarios();
  }, []);

  // Carregar metas quando aba de metas for ativada
  useEffect(() => {
    if (abaAtiva === 'metas') {
      carregarMetas();
    }
  }, [abaAtiva]);

  // Função para ordenar por departamento
  const ordenarPorDepartamento = (usuarios) => {
    const ordemDepartamento = {
      'administrativo': 1,
      'administrador': 1,
      'admin': 2,
      'diretoria': 3,
      'vendas': 4,
      'representante': 5
    };
    
    return [...usuarios].sort((a, b) => {
      const depA = (a.departamento || '').toLowerCase().trim();
      const depB = (b.departamento || '').toLowerCase().trim();
      const ordemA = ordemDepartamento[depA] || 99;
      const ordemB = ordemDepartamento[depB] || 99;
      
      if (ordemA !== ordemB) return ordemA - ordemB;
      // Se mesmo departamento, ordena por nome
      return (a.nome_completo || '').localeCompare(b.nome_completo || '');
    });
  };

  const carregarMetas = async () => {
    setLoadingMetas(true);
    try {
      const response = await api.get('/usuarios/metas');
      if (response.success) {
        // Ordenar por departamento antes de setar
        const usuariosOrdenados = ordenarPorDepartamento(response.usuarios || []);
        setUsuariosMetas(usuariosOrdenados);
        // Inicializar estado de edição com valores atuais
        const editadas = {};
        const originais = {};
        usuariosOrdenados.forEach(u => {
          const valores = {
            meta_junho: u.meta_junho || 0,
            meta_dezembro: u.meta_dezembro || 0,
            meta_inadimplencia: u.meta_inadimplencia || 0,
            forma_analise: u.forma_analise || 'Individual',
            realizado_inadimplencia: u.realizado_inadimplencia || 0,
            media_venda_forro: u.media_venda_forro || 0
          };
          editadas[u.id] = { ...valores };
          originais[u.id] = { ...valores };
        });
        setMetasEditadas(editadas);
        setMetasOriginais(originais);
      }
    } catch (error) {
      console.error('Erro ao carregar metas:', error);
      setModalInfo({
        open: true,
        type: 'error',
        title: 'Erro',
        message: 'Erro ao carregar metas: ' + (error.message || 'Erro desconhecido')
      });
    } finally {
      setLoadingMetas(false);
    }
  };

  // Função para formatar número com máscara brasileira (1.234.567,89)
  const formatarNumeroMascara = (valor) => {
    if (!valor && valor !== 0) return '';
    
    // Converter para string e remover tudo que não é número ou vírgula/ponto
    let valorString = String(valor);
    
    // Se já tem formato brasileiro com ponto e vírgula, extrair o número
    if (valorString.includes('.') && valorString.includes(',')) {
      valorString = valorString.replace(/\./g, '').replace(',', '.');
    } else if (valorString.includes(',')) {
      valorString = valorString.replace(',', '.');
    }
    
    // Remover caracteres não numéricos exceto ponto
    valorString = valorString.replace(/[^\d.]/g, '');
    
    // Separar parte inteira e decimal
    const partes = valorString.split('.');
    let parteInteira = partes[0] || '0';
    let parteDecimal = partes[1] || '';
    
    // Limitar decimal a 2 casas
    parteDecimal = parteDecimal.substring(0, 2);
    
    // Adicionar pontos de milhar na parte inteira
    parteInteira = parteInteira.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Retornar formatado
    if (parteDecimal) {
      return `${parteInteira},${parteDecimal}`;
    }
    return parteInteira;
  };

  // Função para extrair valor numérico de string formatada
  const extrairValorNumerico = (valor) => {
    if (!valor && valor !== 0) return '';
    let valorString = String(valor);
    
    // Remover pontos de milhar e trocar vírgula por ponto
    valorString = valorString.replace(/\./g, '').replace(',', '.');
    
    return valorString;
  };

  // Função para formatar inadimplência (permite vírgula como decimal, sem formatação de milhar)
  const formatarInadimplencia = (valor) => {
    if (!valor && valor !== 0) return '';
    let valorString = String(valor);
    
    // Substituir ponto por vírgula para exibição
    valorString = valorString.replace('.', ',');
    
    // Remover caracteres inválidos (apenas números e vírgula)
    valorString = valorString.replace(/[^\d,]/g, '');
    
    return valorString;
  };

  // Função para processar valor de inadimplência (mantém vírgula/ponto)
  const processarInadimplencia = (valor) => {
    let valorString = String(valor);
    // Permite apenas números e uma vírgula ou ponto
    valorString = valorString.replace(/[^\d.,]/g, '');
    // Substituir vírgula por ponto para armazenamento
    valorString = valorString.replace(',', '.');
    return valorString;
  };

  const handleMetaChange = (usuarioId, campo, valor) => {
    // Para campos numéricos, processar o valor
    let valorProcessado = valor;
    if (campo === 'meta_inadimplencia' || campo === 'realizado_inadimplencia') {
      // Inadimplência: apenas limpa caracteres inválidos, mantém decimal
      valorProcessado = processarInadimplencia(valor);
    } else if (campo !== 'forma_analise') {
      // Valores monetários: extrair valor numérico limpo
      valorProcessado = extrairValorNumerico(valor);
    }
    
    setMetasEditadas(prev => ({
      ...prev,
      [usuarioId]: {
        ...prev[usuarioId],
        [campo]: valorProcessado
      }
    }));
  };

  // Verificar se houve alteração nas metas de um usuário
  const metaFoiAlterada = (usuarioId) => {
    const original = metasOriginais[usuarioId];
    const editada = metasEditadas[usuarioId];
    
    if (!original || !editada) return false;
    
    // Converter para números para comparação
    const parseValor = (v) => parseFloat(String(v || 0).replace(',', '.')) || 0;
    
    return (
      parseValor(original.meta_junho) !== parseValor(editada.meta_junho) ||
      parseValor(original.meta_dezembro) !== parseValor(editada.meta_dezembro) ||
      parseValor(original.meta_inadimplencia) !== parseValor(editada.meta_inadimplencia) ||
      original.forma_analise !== editada.forma_analise ||
      parseValor(original.realizado_inadimplencia) !== parseValor(editada.realizado_inadimplencia) ||
      parseValor(original.media_venda_forro) !== parseValor(editada.media_venda_forro)
    );
  };

  const salvarMeta = async (usuarioId) => {
    setSalvandoMetas(prev => ({ ...prev, [usuarioId]: true }));
    try {
      const metasOriginais = metasEditadas[usuarioId];
      // Converter valores para números antes de enviar
      const metas = {
        ...metasOriginais,
        meta_junho: parseFloat(String(metasOriginais.meta_junho || 0).replace(',', '.')) || 0,
        meta_dezembro: parseFloat(String(metasOriginais.meta_dezembro || 0).replace(',', '.')) || 0,
        meta_inadimplencia: parseFloat(String(metasOriginais.meta_inadimplencia || 0).replace(',', '.')) || 0,
        realizado_inadimplencia: parseFloat(String(metasOriginais.realizado_inadimplencia || 0).replace(',', '.')) || 0,
        media_venda_forro: parseFloat(String(metasOriginais.media_venda_forro || 0).replace(',', '.')) || 0
      };
      const response = await api.put(`/usuarios/${usuarioId}/metas`, metas);
      
      if (response.success) {
        setModalInfo({
          open: true,
          type: 'success',
          title: 'Sucesso!',
          message: 'Metas atualizadas com sucesso!'
        });
        carregarMetas();
      }
    } catch (error) {
      console.error('Erro ao salvar metas:', error);
      setModalInfo({
        open: true,
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar metas: ' + (error.message || 'Erro desconhecido')
      });
    } finally {
      setSalvandoMetas(prev => ({ ...prev, [usuarioId]: false }));
    }
  };

  const carregarUsuarios = async () => {
    setLoading(true);
    setErro('');

    try {
      const response = await api.get('/usuarios');

      if (!response.success) {
        throw new Error(response.message || 'Erro ao carregar usuários');
      }

      // Ordenar por departamento: Admin, Diretoria, Administrativo, Vendas, Representante
      const ordemDepartamento = {
        'admin': 1,
        'diretoria': 2,
        'administrativo': 3,
        'administrador': 3,
        'vendas': 4,
        'representante': 5
      };
      
      const usuariosOrdenados = (response.usuarios || []).sort((a, b) => {
        const depA = (a.departamento || '').toLowerCase().trim();
        const depB = (b.departamento || '').toLowerCase().trim();
        const ordemA = ordemDepartamento[depA] || 99;
        const ordemB = ordemDepartamento[depB] || 99;
        
        if (ordemA !== ordemB) return ordemA - ordemB;
        // Se mesmo departamento, ordena por nome
        return (a.nome_completo || '').localeCompare(b.nome_completo || '');
      });

      setUsuarios(usuariosOrdenados);
    } catch (error) {
      console.error('❌ Erro ao carregar usuários:', error);
      
      let mensagemErro = 'Erro ao carregar usuários.';
      
      if (error.message.includes('Failed to fetch') || error.message === 'Network Error') {
        mensagemErro = '❌ Backend não está respondendo. Verifique se o servidor está rodando.';
      } else {
        mensagemErro = error.message || 'Erro desconhecido';
      }
      
      setErro(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleEditarPermissoes = (usuario) => {
    setUsuarioEditando(usuario);
    setShowModalEditar(true);
  };

  const handleEditarDados = (usuario) => {
    setUsuarioEditando(usuario);
    setShowModalDados(true);
  };



  const handleModalUpdated = () => {
    setShowModalEditar(false);
    setUsuarioEditando(null);
    carregarUsuarios();
  };

  const handleModalDadosUpdated = () => {
    setShowModalDados(false);
    setUsuarioEditando(null);
    carregarUsuarios();
  };

  // Estado para modal de resetar senha
  const [modalResetSenha, setModalResetSenha] = useState({ open: false, usuario: null, senhaGerada: null, loading: false });

  const handleResetarSenha = async (usuario) => {
    setModalResetSenha({ open: true, usuario, senhaGerada: null, loading: true });
    
    try {
      const response = await api.post(`/usuarios/${usuario.id}/resetar-senha`);
      
      if (response.success) {
        setModalResetSenha({ 
          open: true, 
          usuario, 
          senhaGerada: response.senha_provisoria, 
          loading: false 
        });
        carregarUsuarios(); // Atualiza a lista para mostrar o ícone de senha
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      setModalResetSenha({ open: false, usuario: null, senhaGerada: null, loading: false });
      setModalInfo({
        open: true,
        type: 'error',
        title: 'Erro',
        message: error.message || 'Erro ao resetar senha'
      });
    }
  };

  return (
    <ProtecaoAcesso usuario={usuarioLogado}>
      <div className={embedded ? '' : 'px-8 py-6 bg-gray-100 min-h-screen'}>
        <div className={embedded ? '' : 'max-w-7xl mx-auto'}>
        {/* HEADER */}
        <div className="mb-6 animate-fadeIn">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 group cursor-default">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <Users size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Gerenciar Usuários</h1>
                <p className="text-xs text-gray-400">Criar e gerenciar acessos ao sistema</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{usuarios.length}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Cadastrados</p>
            </div>
          </div>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {/* ABAS */}
        <div className="mb-6 flex gap-3 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <button
            className={`px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-300 shadow-sm hover:scale-105 hover:-translate-y-0.5 ${
              abaAtiva === 'todos'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'bg-white text-slate-600 border-2 hover:bg-slate-50 hover:shadow-md'
            }`}
            onClick={() => setAbaAtiva('todos')}
          >
            <Users size={18} />
            Todos os usuários ({usuarios.length})
          </button>

          <button
            className={`px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-300 shadow-sm hover:scale-105 hover:-translate-y-0.5 ${
              abaAtiva === 'novo'
                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                : 'bg-white text-slate-600 border-2 hover:bg-slate-50 hover:shadow-md'
            }`}
            onClick={() => setAbaAtiva('novo')}
          >
            <UserPlus size={18} />
            Novo usuário
          </button>

          <button
            className={`px-6 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all duration-300 shadow-sm hover:scale-105 hover:-translate-y-0.5 ${
              abaAtiva === 'metas'
                ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                : 'bg-white text-slate-600 border-2 hover:bg-slate-50 hover:shadow-md'
            }`}
            onClick={() => setAbaAtiva('metas')}
          >
            <Target size={18} />
            Gerenciar Metas
          </button>
        </div>

        {/* Linha divisória */}
        <div className="w-full h-px bg-gray-200 my-6"></div>

        {erro && (
          <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-xl shadow-sm animate-fadeIn hover:shadow-md transition-all duration-300">
            <div className="flex items-start gap-3">
              <AlertCircle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 mb-2 text-lg">Erro ao carregar dados</p>
                <p className="text-sm text-red-700">{erro}</p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <Loading texto="Carregando usuários..." tamanho="lg" fullScreen={false} />
        ) : (
          <div>
            {abaAtiva === 'todos' && (
              <>
                <TabelaUsuarios 
                  usuarios={usuarios} 
                  onEditarPermissoes={handleEditarPermissoes}
                  onEditarDados={handleEditarDados}
                  onResetarSenha={handleResetarSenha}
                />
                {/* Linha divisória */}
                <div className="w-full h-px bg-gray-200 my-6"></div>
              </>
            )}

            {abaAtiva === 'novo' && (
              <NovoUsuarioForm
                usuarios={usuarios}
                onCriado={() => {
                  carregarUsuarios();
                  setAbaAtiva('todos');
                }}
              />
            )}

            {abaAtiva === 'metas' && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-fadeIn">
                {/* Header da tabela de metas */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <Target size={24} className="text-white" />
                    <div>
                      <h2 className="text-lg font-bold text-white">GERENCIAR METAS</h2>
                      <p className="text-purple-100 text-xs">Configure as metas de vendas para os usuários com permissão de campanhas</p>
                    </div>
                  </div>
                </div>

                {loadingMetas ? (
                  <div className="p-8">
                    <Loading texto="Carregando metas..." tamanho="md" fullScreen={false} />
                  </div>
                ) : usuariosMetas.length === 0 ? (
                  <div className="p-8 text-center">
                    <Target size={48} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">Nenhum usuário com permissão de campanhas</p>
                    <p className="text-gray-400 text-sm mt-2">Ative a opção "Pode ver campanhas" nas permissões dos vendedores</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Usuário</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Desafio Junho</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Desafio Dezembro</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Forma Análise</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Desafio Inadimp. %</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Real. Inadimp. %</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Preço Médio</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usuariosMetas.map((usuario) => (
                          <tr key={usuario.id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-semibold text-gray-800">{usuario.nome_completo}</p>
                                <p className="text-xs text-gray-500">{usuario.departamento}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-gray-500 text-sm">R$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatarNumeroMascara(metasEditadas[usuario.id]?.meta_junho)}
                                  onChange={(e) => handleMetaChange(usuario.id, 'meta_junho', e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleMetaChange(usuario.id, 'meta_junho', pastedText);
                                  }}
                                  placeholder="0"
                                  className="w-32 px-2 py-1 border rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-gray-500 text-sm">R$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatarNumeroMascara(metasEditadas[usuario.id]?.meta_dezembro)}
                                  onChange={(e) => handleMetaChange(usuario.id, 'meta_dezembro', e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleMetaChange(usuario.id, 'meta_dezembro', pastedText);
                                  }}
                                  placeholder="0"
                                  className="w-32 px-2 py-1 border rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <select
                                value={metasEditadas[usuario.id]?.forma_analise || 'Individual'}
                                onChange={(e) => handleMetaChange(usuario.id, 'forma_analise', e.target.value)}
                                className="px-3 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                              >
                                <option value="Individual">Individual</option>
                                <option value="Geral">Geral</option>
                              </select>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatarInadimplencia(metasEditadas[usuario.id]?.meta_inadimplencia)}
                                  onChange={(e) => handleMetaChange(usuario.id, 'meta_inadimplencia', e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleMetaChange(usuario.id, 'meta_inadimplencia', pastedText);
                                  }}
                                  placeholder="0"
                                  className="w-20 px-2 py-1 border rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                                <span className="text-gray-500 text-sm">%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatarInadimplencia(metasEditadas[usuario.id]?.realizado_inadimplencia)}
                                  onChange={(e) => handleMetaChange(usuario.id, 'realizado_inadimplencia', e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleMetaChange(usuario.id, 'realizado_inadimplencia', pastedText);
                                  }}
                                  placeholder="0"
                                  className="w-20 px-2 py-1 border rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                                <span className="text-gray-500 text-sm">%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-gray-500 text-sm">R$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={formatarNumeroMascara(metasEditadas[usuario.id]?.media_venda_forro)}
                                  onChange={(e) => handleMetaChange(usuario.id, 'media_venda_forro', e.target.value)}
                                  onPaste={(e) => {
                                    e.preventDefault();
                                    const pastedText = e.clipboardData.getData('text');
                                    handleMetaChange(usuario.id, 'media_venda_forro', pastedText);
                                  }}
                                  placeholder="0"
                                  className="w-28 px-2 py-1 border rounded text-center text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {metaFoiAlterada(usuario.id) ? (
                                <button
                                  onClick={() => salvarMeta(usuario.id)}
                                  disabled={salvandoMetas[usuario.id]}
                                  className={`px-4 py-2 rounded-lg text-white text-sm font-semibold flex items-center gap-2 mx-auto transition-all ${
                                    salvandoMetas[usuario.id] 
                                      ? 'bg-gray-400 cursor-not-allowed' 
                                      : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg hover:scale-105'
                                  }`}
                                >
                                  <Save size={16} />
                                  {salvandoMetas[usuario.id] ? 'Salvando...' : 'Salvar'}
                                </button>
                              ) : (
                                <span className="text-gray-400 text-sm italic">Sem alterações</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showModalEditar && usuarioEditando && (
          <EditarUsuarioModal
            usuario={usuarioEditando}
            onClose={() => {
              setShowModalEditar(false);
              setUsuarioEditando(null);
            }}
            onUpdated={handleModalUpdated}
          />
        )}

        {showModalDados && usuarioEditando && (
          <EditarDadosModal
            usuario={usuarioEditando}
            onClose={() => {
              setShowModalDados(false);
              setUsuarioEditando(null);
            }}
            onUpdated={handleModalDadosUpdated}
          />
        )}

        {/* Modal de feedback */}
        <Modal
          open={modalInfo.open}
          type={modalInfo.type}
          title={modalInfo.title}
          message={modalInfo.message}
          onClose={() => setModalInfo({ ...modalInfo, open: false })}
        />



        {/* Modal de senha resetada */}
        {modalResetSenha.open && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/50" onClick={() => !modalResetSenha.loading && setModalResetSenha({ open: false, usuario: null, senhaGerada: null, loading: false })} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10 animate-scaleIn">
              <button
                onClick={() => setModalResetSenha({ open: false, usuario: null, senhaGerada: null, loading: false })}
                disabled={modalResetSenha.loading}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X size={24} />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Key className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Resetar Senha</h2>
                  <p className="text-sm text-gray-500">{modalResetSenha.usuario?.nome_completo}</p>
                </div>
              </div>

              {modalResetSenha.loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Gerando nova senha...</p>
                </div>
              ) : modalResetSenha.senhaGerada ? (
                <div>
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-700 mb-2 font-semibold">Nova senha provisória gerada:</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white border-2 border-amber-300 rounded-lg px-4 py-3">
                        <code className="text-lg font-mono font-bold text-amber-800">{modalResetSenha.senhaGerada}</code>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(modalResetSenha.senhaGerada);
                          setModalInfo({ open: true, type: 'success', title: 'Copiado!', message: 'Senha copiada para a área de transferência' });
                        }}
                        className="px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center gap-2 font-semibold"
                      >
                        <Copy size={18} />
                        Copiar
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <p className="text-sm text-blue-800 flex items-start gap-2">
                      <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>IMPORTANTE:</strong> Envie esta senha ao usuário. No próximo login, ele será <strong>obrigado a criar uma nova senha</strong>.
                      </span>
                    </p>
                  </div>

                  <button
                    onClick={() => setModalResetSenha({ open: false, usuario: null, senhaGerada: null, loading: false })}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all"
                  >
                    Fechar
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        )}

        </div>
      </div>
    </ProtecaoAcesso>
  );
};

export default GerenciarUsuarios;